import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Lightning, Crown, CheckCircle, FilePdf, Buildings, Trophy, Users,
  Sparkle, ArrowRight, ShieldCheck, Heart, Calendar, SealCheck, ChartLineUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import EmailAuthModal from "../components/EmailAuthModal";

const LocalHero = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    axios.get(`${API}/local-hero/leaderboard`)
      .then((r) => setLeaderboard(r.data.leaderboard || []))
      .catch(() => {});
  }, []);
  const [form, setForm] = useState({
    company: "",
    contact_name: "",
    title: "",
    email: user?.email || "",
    phone: "",
    annual_budget: "$5,000 (Local Hero)",
    message: "",
  });

  // Pull ref code from query string if present (Wardell-style attribution)
  const refCode = new URLSearchParams(window.location.search).get("ref") || "";

  const benefits = [
    {
      icon: <SealCheck weight="duotone" className="w-6 h-6 text-emerald-400" />,
      title: "Named recognition",
      desc: "Your business name appears on the nonprofit's website, event signage, programs, and marketing materials as an annual partner."
    },
    {
      icon: <Buildings weight="duotone" className="w-6 h-6 text-emerald-400" />,
      title: "Custom Local Hero microsite",
      desc: "A branded page at flash-grants.com/local-hero/[your-business] showing who you sponsor, what programs you fund, and impact driven."
    },
    {
      icon: <ChartLineUp weight="duotone" className="w-6 h-6 text-emerald-400" />,
      title: "Monthly traffic report",
      desc: "We track every visit, phone tap, and 'directions' click your microsite drives to your business — and email you the numbers on the 1st of every month. Proof your sponsorship works."
    },
    {
      icon: <FilePdf weight="duotone" className="w-6 h-6 text-emerald-400" />,
      title: "Quarterly Impact PDF",
      desc: "Audit-ready, letter-size, branded report delivered Apr 1 / Jul 1 / Oct 1 / Jan 1 covering all sponsorships paid out that quarter."
    },
    {
      icon: <ShieldCheck weight="duotone" className="w-6 h-6 text-emerald-400" />,
      title: "Verifiable receipts",
      desc: "Nonprofit acknowledgement letters cross-referenced and stored alongside each entry — built for franchisor or accountant audits."
    },
    {
      icon: <Users weight="duotone" className="w-6 h-6 text-emerald-400" />,
      title: "Concierge introductions",
      desc: "We personally introduce you to 3 South Carolina nonprofits matching your industry, brand pillars, or franchise community guidelines."
    },
    {
      icon: <Trophy weight="duotone" className="w-6 h-6 text-emerald-400" />,
      title: "Leaderboard placement",
      desc: "Featured alongside other SC Local Heroes — visible social proof for your customers and corporate HQ."
    },
    {
      icon: <Sparkle weight="duotone" className="w-6 h-6 text-emerald-400" />,
      title: "Digital badge",
      desc: 'Embeddable on your website and email signature: "Proud SC Local Hero — flash-grants.com".'
    },
    {
      icon: <Calendar weight="duotone" className="w-6 h-6 text-emerald-400" />,
      title: "Annual renewal call",
      desc: "One 30-minute strategy call each year to review impact, adjust focus areas, and plan next year's giving."
    },
  ];

  const submit = async () => {
    if (!form.company.trim() || !form.contact_name.trim() || !form.email.trim()) {
      toast.error("Company, your name, and email are required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/corporate/inquiry`, {
        ...form,
        tier_interest: "Local Hero",
        focus_areas: [],
        ref_code: refCode,
      });
      setSubmitted(true);
      toast.success("Thanks — we'll reach out within 1 business day");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send. Try again or email hello@flash-grants.com");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-black text-zinc-100" data-testid="local-hero-page">
      {/* Header */}
      <header className="border-b border-zinc-900">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Lightning weight="fill" className="w-6 h-6 text-emerald-500" />
            <span className="font-bold tracking-tight">FlashGrants</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/sponsorships" className="text-sm text-zinc-400 hover:text-zinc-100 transition px-3 py-1.5 rounded-sm">
              Browse sponsorships
            </Link>
            {!user && (
              <Button
                onClick={() => setShowAuth(true)}
                variant="outline"
                size="sm"
                className="border-zinc-800 hover:border-emerald-500/40 hover:text-emerald-300 rounded-sm"
              >
                Sign in
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-5 pt-20 pb-12">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-400 font-mono mb-5">
            <Crown weight="fill" className="w-3.5 h-3.5" /> Local Hero · $5,000/yr
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-5 leading-tight">
            Turn local giving into a<br/>
            <span className="text-emerald-400">verifiable annual partnership.</span>
          </h1>
          <p className="text-lg text-zinc-400 max-w-2xl leading-relaxed mb-8">
            For South Carolina small businesses and franchisees who want named recognition with SC nonprofits — and an audit-ready quarterly Impact PDF they can hand to franchisor HQ or their accountant without follow-up.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => document.getElementById("inquiry")?.scrollIntoView({ behavior: "smooth" })}
              className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm px-6 py-5 text-base"
              data-testid="hero-cta"
            >
              <Heart weight="fill" className="w-4 h-4 mr-2" />
              Request a 15-minute call
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              onClick={() => navigate("/sponsorships")}
              variant="outline"
              className="border-zinc-800 hover:border-emerald-500/40 hover:text-emerald-300 rounded-sm px-6 py-5 text-base"
            >
              See the free marketplace first
            </Button>
          </div>
        </div>
      </section>

      {/* Compliance hook */}
      <section className="border-y border-zinc-900 bg-emerald-500/5">
        <div className="max-w-5xl mx-auto px-5 py-10">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            <div>
              <p className="text-3xl font-black text-emerald-400 mb-1">$5K/yr</p>
              <p className="text-xs uppercase tracking-wider text-zinc-500 font-mono">Flat annual fee</p>
            </div>
            <div>
              <p className="text-3xl font-black text-emerald-400 mb-1">4</p>
              <p className="text-xs uppercase tracking-wider text-zinc-500 font-mono">Audit-ready PDFs per year</p>
            </div>
            <div>
              <p className="text-3xl font-black text-emerald-400 mb-1">12</p>
              <p className="text-xs uppercase tracking-wider text-zinc-500 font-mono">Monthly traffic reports</p>
            </div>
            <div>
              <p className="text-3xl font-black text-emerald-400 mb-1">3</p>
              <p className="text-xs uppercase tracking-wider text-zinc-500 font-mono">Concierge nonprofit intros</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits grid */}
      <section className="max-w-6xl mx-auto px-5 py-16">
        <h2 className="text-xs uppercase tracking-widest text-emerald-400 font-mono mb-3">What you receive</h2>
        <p className="text-2xl sm:text-3xl font-bold tracking-tight mb-8 max-w-2xl">
          Everything you need to make community giving look professional, intentional, and audit-ready.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          {benefits.map((b) => (
            <Card key={b.title} className="bg-zinc-950 border-zinc-800 hover:border-emerald-500/30 transition-colors">
              <CardContent className="py-5 flex gap-4">
                <div className="flex-shrink-0">{b.icon}</div>
                <div>
                  <p className="font-bold text-zinc-100 mb-1">{b.title}</p>
                  <p className="text-sm text-zinc-400 leading-relaxed">{b.desc}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Who this is for */}
      <section className="border-t border-zinc-900 bg-zinc-950/50">
        <div className="max-w-5xl mx-auto px-5 py-16">
          <h2 className="text-xs uppercase tracking-widest text-emerald-400 font-mono mb-3">Built for</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { title: "Franchisees with HQ pressure", desc: "Corporate wants documented community giving. The quarterly PDF satisfies it without follow-up." },
              { title: "SMB owners seeking visibility", desc: "Logo on event signage, mentions in nonprofit newsletters, microsite, leaderboard — for the price of one trade-show booth." },
              { title: "Multi-location operators", desc: "One Local Hero subscription covers giving across all locations and produces one clean quarterly report." },
            ].map((p) => (
              <div key={p.title} className="bg-zinc-950 border border-zinc-800 rounded-sm p-5">
                <div className="text-emerald-400 mb-2"><CheckCircle weight="fill" className="w-5 h-5" /></div>
                <p className="font-bold text-zinc-100 mb-1">{p.title}</p>
                <p className="text-sm text-zinc-400 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SC Local Heroes leaderboard */}
      {leaderboard.length > 0 && (
        <section className="border-t border-zinc-900 bg-zinc-950/50" data-testid="leaderboard-section">
          <div className="max-w-5xl mx-auto px-5 py-16">
            <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
              <div>
                <h2 className="text-xs uppercase tracking-widest text-emerald-400 font-mono mb-2 flex items-center gap-1.5">
                  <Trophy weight="fill" className="w-4 h-4" /> SC Local Heroes Leaderboard
                </h2>
                <p className="text-2xl sm:text-3xl font-bold tracking-tight max-w-2xl">Real businesses, real giving, real receipts.</p>
              </div>
            </div>
            <div className="space-y-2">
              {leaderboard.map((b, i) => (
                <Link key={b.slug} to={`/local-hero/${b.slug}`} className="block">
                  <Card className="bg-zinc-950 border-zinc-800 hover:border-emerald-500/40 transition-colors">
                    <CardContent className="py-4 flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-4 flex-1 min-w-[200px]">
                        <span className="text-2xl font-black text-zinc-700 font-mono w-8 text-center">#{i + 1}</span>
                        <div>
                          <p className="font-bold text-zinc-100">{b.business_name}</p>
                          {b.location && <p className="text-xs text-zinc-500">{b.location}</p>}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-emerald-400 font-bold font-mono text-lg">${b.total_dollars.toLocaleString()}</p>
                        <p className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider">{b.log_count} contributions</p>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Inquiry form */}
      <section id="inquiry" className="border-t border-zinc-900">
        <div className="max-w-2xl mx-auto px-5 py-16">
          <h2 className="text-xs uppercase tracking-widest text-emerald-400 font-mono mb-3">Request a 15-minute call</h2>
          <h3 className="text-2xl sm:text-3xl font-bold tracking-tight mb-6">
            Tell us about your business — we'll show you the dashboard, the PDF format, and which SC nonprofits fit your industry.
          </h3>
          {submitted ? (
            <Card className="bg-emerald-500/5 border-emerald-500/30">
              <CardContent className="py-10 text-center">
                <CheckCircle weight="fill" className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                <p className="text-xl font-bold mb-2">Thanks — we'll be in touch</p>
                <p className="text-sm text-zinc-400">We'll reach out within 1 business day at <strong className="text-zinc-200">{form.email}</strong>.</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-zinc-950 border-zinc-800">
              <CardContent className="py-6 space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs">Business name</Label>
                    <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Smith Family Franchise LLC" className="bg-zinc-900 border-zinc-800" data-testid="inquiry-company" /></div>
                  <div><Label className="text-xs">Your name</Label>
                    <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="bg-zinc-900 border-zinc-800" data-testid="inquiry-name" /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs">Email</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-zinc-900 border-zinc-800" data-testid="inquiry-email" /></div>
                  <div><Label className="text-xs">Phone <span className="text-zinc-600">(optional)</span></Label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-zinc-900 border-zinc-800" data-testid="inquiry-phone" /></div>
                </div>
                <div><Label className="text-xs">Your title <span className="text-zinc-600">(optional)</span></Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Owner / Franchisee / Marketing Director" className="bg-zinc-900 border-zinc-800" data-testid="inquiry-title" /></div>
                <div><Label className="text-xs">Anything specific we should know? <span className="text-zinc-600">(optional)</span></Label>
                  <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} placeholder="Number of locations, franchise brand, specific causes you care about, etc." className="bg-zinc-900 border-zinc-800 text-sm" data-testid="inquiry-message" /></div>
                <Button onClick={submit} disabled={submitting} className="w-full bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm py-5" data-testid="inquiry-submit">
                  {submitting ? "Sending…" : "Request a call"}
                </Button>
                <p className="text-[11px] text-zinc-600 text-center">
                  We respond within 1 business day. No spam, no auto-charge.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-900 py-8">
        <div className="max-w-6xl mx-auto px-5 text-center text-xs text-zinc-600 space-y-1">
          <p>FlashGrants · flash-grants.com · South Carolina's nonprofit + local-business marketplace</p>
          <p>
            <Link to="/sponsorships" className="text-zinc-400 hover:text-emerald-300">Browse sponsorships</Link>
            {" · "}
            <Link to="/pricing" className="text-zinc-400 hover:text-emerald-300">All plans</Link>
            {" · "}
            <a href="mailto:hello@flash-grants.com" className="text-zinc-400 hover:text-emerald-300">hello@flash-grants.com</a>
          </p>
        </div>
      </footer>

      {showAuth && <EmailAuthModal onClose={() => setShowAuth(false)} />}
    </div>
  );
};

export default LocalHero;
